import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: legalDocument,
  onClose
}: LegalModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isOpen = legalDocument !== null;
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-171" data-mp="1aqgj1vauihwn">
      {legalDocument && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-172" data-mp="1apn6uet4b074">
          <motion.div className="absolute inset-0 bg-ink/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} aria-hidden="true" data-id="element-173" data-mp="1fo87gfca7lw3" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-lg bg-white shadow-2xl sm:rounded-lg" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-174" data-mp="rxuf0npsbl0z">
            <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5" data-id="element-175" data-mp="e67jiq3o2gj9">
              <div data-id="element-176" data-mp="18rihqzpyp0w">
                <h2 id="legal-modal-title" className="font-serif text-2xl font-bold tracking-tight text-ink" data-id="element-177" data-mp="19b3xhokvvjo">
                  {legalDocument.title}
                </h2>
                <p className="mt-1 font-sans text-xs text-ink-muted" data-id="element-178" data-mp="azvy8sx7ydws">
                  {legalDocument.updatedLabel}
                </p>
              </div>
              <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-ink-soft transition-colors duration-150 ease-out hover:bg-neutral-100 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-179" data-mp="3efb8nv9iepig">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-180" data-mp="13o1i7dgg7yvk" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6" data-id="element-181" data-mp="1b5pyzmm7aope">
              {legalDocument.sections.map(section => <section key={section.heading} className="mb-6 last:mb-0" data-id="element-182" data-mp="1g6nf7tnnh8o9">
                  <h3 className="font-sans text-sm font-bold uppercase tracking-wide text-ink" data-id="element-183" data-mp="126uuceyt1mo7">
                    {section.heading}
                  </h3>
                  {section.paragraphs.map((paragraph, index) => <p key={index} className="mt-2 font-serif text-[0.97rem] leading-relaxed text-ink-soft" data-id="element-184" data-mp="1txsjxa5vkr5g" data-mpi={index}>
                      {paragraph}
                    </p>)}
                </section>)}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}